import { useEffect } from 'react';

import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import {
  ROUTES,
  SIDEBAR_ADMIN_SUBMENU_LINKS,
  SIDEBAR_INTEGRATIONS_SUBMENU_LINKS,
  SIDEBAR_TENANT_ADMINISTRATION_SUBMENU_LINKS,
} from 'constants/routes';
import { UserTypes } from 'constants/user';
import { SupportRequestChatProvider } from 'contexts/SupportRequestChatContext';
import useSubscriptionInformation from 'hooks/useSubscriptionInformation';
import useUserRole from 'hooks/useUserRole';
import { getAuthState } from 'modules/auth/action';
import { selectIsAuth } from 'modules/auth/selector';
import { useAppDispatch, useAppSelector } from 'modules/store';
import { DashboardFilters } from 'page-components/dashboard/dashboard-filters/DashboardFilters';
//<#if (Reporting)
import CreateReportTemplatePage from 'page-components/report-templates/CreateReportTemplatePage';
import RunReportTemplatePage from 'page-components/report-templates/RunReportTemplatePage';
//#endif
import {
  ActivateEmail,
  AuditLogs,
  CloneEmailTemplate,
  //<#if (CodatAccounting)
  CodatAccounting,
  //#endif
  //<#if (CompaniesHouse)
  CompaniesHouse,
  //#endif
  ConfirmEmail,
  CreateEmailTemplate,
  //<#if (QuickBooks)
  CustomerDetailsPage,
  //#endif
  //<#if (Docusign)
  CypressTestsPage,
  //#endif
  Dashboard,
  //<#if (Docusign)
  Docusign,
  DocusignDocumentEditorPage,
  //#endif
  EditEmailTemplate,
  EmailTemplates,
  ForgotPassword,
  GDPR,
  //<#if (GoCardless)
  GoCardless,
  //#endif
  InvoiceDetailsPage,
  //<#if (QuickBooks)
  InvoiceQuickBooksDetailsPage,
  //#endif
  Invoices,
  ManagementPage,
  NotFound,
  OrganizationUnits,
  //<#if (Opensearch)
  OSAuditLogs,
  //#endif
  Overview,
  //<#if (PayPal)
  PayPal,
  //#endif
  PrivacyPolicy,
  //<#if (QuickBooks)
  QuickBooks,
  //#endif
  ResetPassword,
  Roles,
  //<#if (Sage)
  Sage,
  SageContactDetailsPage,
  SageInvoiceDetailsPage,
  //#endif
  Samples,
  SendEmailTemplate,
  SignIn,
  SignUp,
  SubscriptionPaymentCompleted,
  SubscriptionPaymentNotCompleted,
  SubscriptionPurchase,
  SubscriptionsAdmin,
  SubscriptionSelect,
  SubscriptionsTenant,
  SubscriptionStripePurchase,
  SubscriptionUpgrade,
  SupportRequests,
  SupportTicketChat,
  SupportTickets,
  TenantDetails,
  TenantDetailsPage,
  Tenants,
  //<#if (TrueLayer)
  TrueLayer,
  TrueLayerAccountDetailsPage,
  TrueLayerCardDetailsPage,
  //#endif
  TwoFactorAuthenticator,
  //<#if (Typless)
  TyplessDocumentDetailsTypesPage,
  TyplessDocumentParsingPage,
  TyplessDocumentTypesPage,
  //#endif
  Users,
  //<#if (Xero)
  Xero,
  //#endif
  EmailHistory,
  //<#if (Reporting)
  ReportConfiguration,
  //#endif
} from 'pages';
//<#if (Reporting)
import ReportDocumentBuilder from 'pages/ReportDocumentBuilder';
import ReportTemplates from 'pages/ReportTemplates';
//#endif
import { ApplicationLayout, AuthLayout, ProtectedRoute } from 'shared-components';
import PermissionProtectedRoute from 'shared-components/PermissionProtectedRoute';

import TermsAndConditions from './pages/TermsAndConditions';
import AppContentLayout from './shared-components/AppContentLayout';

const findPermissionTagForRoute = (routePath: string, userType: string): string | null => {
  let sidebarItems;

  if (userType === UserTypes.ADMIN) {
    sidebarItems = [...SIDEBAR_ADMIN_SUBMENU_LINKS, ...SIDEBAR_INTEGRATIONS_SUBMENU_LINKS];
  } else {
    sidebarItems = [...SIDEBAR_TENANT_ADMINISTRATION_SUBMENU_LINKS, ...SIDEBAR_INTEGRATIONS_SUBMENU_LINKS];
  }

  const foundItem = sidebarItems.find((item) => {
    if (item.path === routePath) {
      return true;
    }
    if (routePath.includes('/*')) {
      const baseRoute = routePath.replace('/*', '');
      return item.path === baseRoute;
    }
    return false;
  });

  return foundItem?.permissionTag || null;
};

export const UNPROTECTED_ROUTES = [
  { path: ROUTES.signIn, element: SignIn },
  { path: ROUTES.signUp, element: SignUp },
  { path: ROUTES.forgotPassword, element: ForgotPassword },
  { path: ROUTES.activateEmail, element: ActivateEmail },
  { path: ROUTES.resetPassword, element: ResetPassword },
  { path: ROUTES.confirmEmail, element: ConfirmEmail },
  { path: ROUTES.twoFactorAuthenticator, element: TwoFactorAuthenticator },
  //<#if (Docusign)
  { path: ROUTES.cypressTestsPage, element: CypressTestsPage },
  //#endif
];

export const PROTECTED_SHARED_ROUTES = [
  {
    path: ROUTES.dashboard,
    element: Dashboard,
    pageTitle: 'Dashboard',
    customHeader: <DashboardFilters />,
  },
  { path: ROUTES.overview, element: Overview },
  { path: ROUTES.roles, element: Roles, hideLayout: true },
  { path: ROUTES.users, element: Users, hideLayout: true },
  { path: ROUTES.managementPage, element: ManagementPage },
  { path: ROUTES.auditLogs, element: AuditLogs, pageTitle: 'AuditLogs' },
  { path: ROUTES.samples, element: Samples, pageTitle: 'SamplePage' },
  { path: ROUTES.supportTicketsChat, element: SupportTicketChat, hideLayout: true },
  //<#if (CompaniesHouse)
  { path: ROUTES.companiesHouse, element: CompaniesHouse, pageTitle: 'CompaniesHouse' },
  //#endif
  //<#if (TrueLayer)
  { path: ROUTES.trueLayer, element: TrueLayer, pageTitle: 'TrueLayer' },
  //#endif
  //<#if (Xero)
  { path: `${ROUTES.xero}/*`, element: Xero, pageTitle: 'Xero' },
  //#endif
  //<#if (CodatAccounting)
  {
    path: `${ROUTES.codatAccounting}/*`,
    element: CodatAccounting,
    pageTitle: 'CodatAccounting',
  },
  //#endif
  //<#if (GoCardless)
  {
    path: `${ROUTES.goCardless}/*`,
    element: GoCardless,
    pageTitle: 'Repayments',
    titlePrefix: 'GoCardless: ',
  },
  //#endif
  //<#if (Sage)
  {
    path: `${ROUTES.sage}/*`,
    element: Sage,
    pageTitle: 'Sage',
  },
  //#endif
  //<#if (QuickBooks)
  { path: `${ROUTES.quickBooks}/*`, element: QuickBooks, pageTitle: 'QuickBooks' },
  //#endif
  //<#if (PayPal)
  { path: `${ROUTES.payPal}/*`, element: PayPal, pageTitle: 'PayPal' },
  //#endif
  //<#if (Docusign)
  { path: `${ROUTES.docusign}/*`, element: Docusign },
  //#endif
];

export const PROTECTED_ADMIN_ROUTES = [
  { path: ROUTES.tenants, element: Tenants, pageTitle: 'Tenants' },
  { path: ROUTES.tenantUsers, element: Users, hideLayout: true },
  { path: ROUTES.tenantRoles, element: Roles, hideLayout: true },
  { path: ROUTES.gdprAdmin, element: GDPR, pageTitle: 'TenantRequestsToBeForgotten' },
  { path: ROUTES.supportTickets, element: SupportTickets, pageTitle: 'SupportTickets' },
  {
    path: ROUTES.tenantDetails,
    element: TenantDetails,
    pageTitle: 'TenantDetails',
    customHeader: '',
  },
  //<#if (Reporting)
  {
    path: ROUTES.reportConfiguration,
    element: ReportConfiguration,
    pageTitle: 'ReportConfiguration',
  },
  {
    path: ROUTES.reportDocumentBuilder,
    element: ReportDocumentBuilder,
    bodyClassName: 'py-0 px-0',
  },
  {
    path: ROUTES.reportTemplates,
    element: ReportTemplates,
    pageTitle: 'ReportTemplates',
  },
  {
    path: '/management/report-templates/:id/edit',
    element: CreateReportTemplatePage,
  },
  {
    path: ROUTES.reportTemplatesCreate,
    element: CreateReportTemplatePage,
  },
  {
    path: '/management/report-templates/:id/run',
    element: RunReportTemplatePage,
  },
  //#endif
  {
    path: ROUTES.emailTemplates,
    element: EmailTemplates,
    pageTitle: 'EmailTemplates',
  },
  {
    path: ROUTES.emailHistory,
    element: EmailHistory,
    pageTitle: 'EmailHistory',
  },
  { path: ROUTES.createEmailTemplate, element: CreateEmailTemplate, pageTitle: 'CreateEmailTemplate' },
  { path: ROUTES.sendEmailTemplate, element: SendEmailTemplate, pageTitle: 'SendEmail' },
  { path: ROUTES.sendEmailTemplateById, element: SendEmailTemplate, pageTitle: 'SendEmail' },
  { path: ROUTES.sendEmailTemplate, element: SendEmailTemplate, pageTitle: 'SendEmail' },
  { path: ROUTES.editEmailTemplate, element: EditEmailTemplate, pageTitle: 'EditEmailTemplate' },
  { path: ROUTES.cloneEmailTemplate, element: CloneEmailTemplate, pageTitle: 'CloneEmailTemplate' },
  { path: ROUTES.subscriptions, element: SubscriptionsAdmin, pageTitle: 'Subscriptions' },
  { path: ROUTES.storybook, pageTitle: 'Storybook', hideLayout: true, element: () => null },
];

export const PROTECTED_TENANT_ROUTES = [
  { path: ROUTES.tenantSubscription, element: SubscriptionsTenant, pageTitle: 'Subscription' },
  { path: ROUTES.supportRequests, element: SupportRequests, pageTitle: 'SupportRequests' },
  { path: ROUTES.tenantDetailsPage, element: TenantDetailsPage, pageTitle: 'TenantDetails' },
  { path: ROUTES.gdprTenant, element: GDPR, pageTitle: 'UserRequestsToBeForgotten' },
  { path: ROUTES.organizationUnits, element: OrganizationUnits, pageTitle: 'OrganizationUnits' },
  { path: ROUTES.invoices, element: Invoices, hideLayout: true },
  { path: ROUTES.invoiceDetailsPage, element: InvoiceDetailsPage, hideLayout: true },
  //<#if (TrueLayer)
  {
    path: ROUTES.trueLayerAccountDetails,
    element: TrueLayerAccountDetailsPage,
    pageTitle: 'TrueLayerAccountDetails',
  },
  {
    path: ROUTES.trueLayerCardDetails,
    element: TrueLayerCardDetailsPage,
    pageTitle: 'TrueLayerCardDetails',
  },
  //#endif
  //<#if (QuickBooks)
  { path: ROUTES.customerDetailsPage, element: CustomerDetailsPage, hideLayout: true },
  { path: ROUTES.invoiceQuickBooksDetailsPage, element: InvoiceQuickBooksDetailsPage, hideLayout: true },
  //#endif
  //<#if (Sage)
  {
    path: ROUTES.sageInvoiceDetails,
    element: SageInvoiceDetailsPage,
    pageTitle: 'InvoiceDetails',
  },
  {
    path: ROUTES.sageContactDetails,
    element: SageContactDetailsPage,
    pageTitle: 'ContactDetails',
  },
  //#endif
  //<#if (Typless)
  {
    path: ROUTES.typlessDocumentTypesPage,
    element: TyplessDocumentTypesPage,
    pageTitle: 'DocumentTypes',
  },
  { path: ROUTES.typlessTrainDocumentPage, element: TyplessDocumentDetailsTypesPage, hideLayout: true },
  { path: ROUTES.typlessDocumentParsingPage, element: TyplessDocumentParsingPage, pageTitle: 'DocumentParsing' },
  //#endif
  //<#if (Opensearch)
  { path: ROUTES.osAuditLogs, element: OSAuditLogs, hideLayout: true },
  //#endif
  //<#if (Docusign)
  { path: ROUTES.docusignNewDocument, element: DocusignDocumentEditorPage, pageTitle: 'DocumentEditor' },
  //#endif
  //<#if (QuickBooks)
  { path: ROUTES.quickBooksCallback, element: QuickBooks, pageTitle: 'QuickBooks' },
  //#endif
  //<#if (Xero)
  { path: ROUTES.xeroCallback, element: Xero, pageTitle: 'Xero' },
  //#endif
  { path: ROUTES.subscriptionUpgrade, element: SubscriptionUpgrade, hideLayout: true },
  { path: ROUTES.subscriptionPurchase, element: SubscriptionPurchase, hideLayout: true },
  { path: ROUTES.subscriptionStripePurchase, element: SubscriptionStripePurchase, hideLayout: true },
  {
    path: ROUTES.subscriptionPaymentCompleted,
    element: SubscriptionPaymentCompleted,
    hideLayout: true,
  },
  {
    path: ROUTES.subscriptionPaymentNotCompleted,
    element: SubscriptionPaymentNotCompleted,
    hideLayout: true,
  },
];

export const SHARED_ROUTES = [
  { path: ROUTES.notFound, element: NotFound },
  {
    path: ROUTES.termsAndConditions,
    element: TermsAndConditions,
  },
  { path: ROUTES.privacyPolicy, element: PrivacyPolicy },
  {
    path: ROUTES.subscriptionSelect,
    element: SubscriptionSelect,
    pageTitle: 'ChooseSubscription',
    hideLayout: true,
  },
];

const Router = () => {
  const { isSubscription } = useSubscriptionInformation();
  const { userRole } = useUserRole();

  const dispatch = useAppDispatch();
  const isAuth = useAppSelector(selectIsAuth);

  function getRedirectRoutePath(isAuth: boolean): string {
    return isAuth ? ROUTES.dashboard : ROUTES.signIn;
  }

  useEffect(() => {
    if (isAuth === null) {
      dispatch(getAuthState());
    }
  }, [dispatch, isAuth, isSubscription]);

  if (isAuth === null) {
    return null;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path={ROUTES.default} element={<Navigate to={getRedirectRoutePath(isAuth)} />} />

        {SHARED_ROUTES.map(({ path, element: Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}

        <Route element={<ProtectedRoute isAllowed={!isAuth} redirectPath={ROUTES.dashboard} />}>
          <Route element={<AuthLayout />}>
            {UNPROTECTED_ROUTES.map(({ path, element: Component }) => (
              <Route key={path} path={path} element={<Component />} />
            ))}
          </Route>
        </Route>

        <Route element={<ProtectedRoute isAllowed={isAuth} redirectPath={ROUTES.signIn} />}>
          {PROTECTED_SHARED_ROUTES.map(
            ({
              path,
              element: Component,
              hideLayout,
              pageTitle,
              //<#if (GoCardless)
              titlePrefix,
              //#endif
              customHeader,
            }) => {
              const permissionTag = findPermissionTagForRoute(path, userRole);

              return (
                <Route
                  key={path}
                  element={
                    <PermissionProtectedRoute
                      permissionTag={permissionTag || undefined}
                      redirectPath={ROUTES.dashboard}
                    />
                  }
                >
                  <Route
                    key={path}
                    element={
                      <SupportRequestChatProvider>
                        <ApplicationLayout pageTitle={pageTitle} />
                      </SupportRequestChatProvider>
                    }
                  >
                    <Route
                      key={path}
                      path={path}
                      element={
                        hideLayout ? (
                          <Component />
                        ) : (
                          <AppContentLayout
                            title={pageTitle}
                            //<#if (GoCardless)
                            titlePrefix={titlePrefix}
                            //#endif
                            customHeader={customHeader}
                          >
                            <Component />
                          </AppContentLayout>
                        )
                      }
                    />
                  </Route>
                </Route>
              );
            }
          )}
        </Route>

        <Route
          element={
            <ProtectedRoute
              isAllowed={isAuth}
              allowedRoles={['admin']}
              redirectPath={isAuth ? ROUTES.notFound : ROUTES.signIn}
            />
          }
        >
          {PROTECTED_ADMIN_ROUTES.map(
            ({
              path,
              element: Component,
              pageTitle,
              hideLayout,
              customHeader: CustomHeader,
              //<#if (Reporting)
              bodyClassName,
              //#endif
            }) => (
              <Route key={path} element={<ApplicationLayout pageTitle={pageTitle} />}>
                <Route
                  key={path}
                  path={path}
                  element={
                    hideLayout ? (
                      <Component />
                    ) : (
                      <AppContentLayout
                        title={pageTitle}
                        customHeader={CustomHeader && <CustomHeader />}
                        //<#if (Reporting)
                        bodyClassName={bodyClassName}
                        //#endif
                      >
                        <Component />
                      </AppContentLayout>
                    )
                  }
                />
              </Route>
            )
          )}
        </Route>

        <Route
          element={
            <ProtectedRoute
              isAllowed={isAuth && isSubscription}
              allowedRoles={['tenant']}
              redirectPath={getRedirectRoutePath(isAuth)}
            />
          }
        >
          {PROTECTED_TENANT_ROUTES.map(({ path, element: Component, hideLayout, pageTitle }) => (
            <Route key={path} element={<ApplicationLayout pageTitle={pageTitle} />}>
              <Route
                path={path}
                element={
                  hideLayout ? (
                    <Component />
                  ) : (
                    <AppContentLayout title={pageTitle}>
                      <Component />
                    </AppContentLayout>
                  )
                }
              />
            </Route>
          ))}
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default Router;
